import type { ReactNode } from 'react';
import type { ColumnDef, HeaderContext, VisibilityState } from '@tanstack/react-table';
import { Badge } from '../ui/badge';
import { SortableHeader } from '../shared/SortableHeader';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { isTenantSystemRole } from '../../lib/systemRoles';
import type { LabelResolver } from '../../hooks/useLabelResolver';
import type { TenantRoleListItemLocal } from '../../api/types/TenantLocal';

/** El roleId no se muestra: su columna queda oculta y solo alimenta el buscador. */
export const ROLE_TABLE_HIDDEN_COLUMNS: VisibilityState = { roleId: false };

/** Lo mínimo que una fila de rol necesita para las columnas comunes. */
interface RoleRowBase {
    roleId: string;
    permissions?: unknown[];
}

interface RoleColumnsCoreOptions<T extends RoleRowBase> {
    getName: (role: T) => string;
    getScopeLabel: (role: T) => string;
    isSystem: (role: T) => boolean;
    renderActions: (role: T, isSystem: boolean) => ReactNode;
}

interface RoleColumnsOptions {
    labels: LabelResolver;
    renderActions: (role: TenantRoleListItemLocal, isSystem: boolean) => ReactNode;
}

function headerWithHint<T>(title: string, hint: string) {
    return function Header({ column }: HeaderContext<T, unknown>) {
        return (
            <span className="inline-flex items-center justify-center gap-1">
                <SortableHeader column={column} title={title} />
                <HelpHint text={hint} side="right" />
            </span>
        );
    };
}

/** Columnas comunes a las tablas de roles: Rol · Alcance · Tipo · Permisos · Acciones. */
export function buildRoleColumnsCore<T extends RoleRowBase>({
    getName,
    getScopeLabel,
    isSystem,
    renderActions,
}: RoleColumnsCoreOptions<T>): ColumnDef<T>[] {
    return [
        { id: 'roleName', accessorFn: getName, header: headerWithHint<T>('Rol', HELP_TEXT.role) },
        { id: 'roleId', accessorFn: (row) => row.roleId, enableSorting: false },
        {
            id: 'scope',
            accessorFn: getScopeLabel,
            header: headerWithHint<T>('Alcance', HELP_TEXT.scope),
            cell: (info) => <Badge variant="outline">{info.getValue() as string}</Badge>,
        },
        {
            id: 'type',
            accessorFn: (row) => (isSystem(row) ? 'Sistema' : 'Personalizado'),
            header: headerWithHint<T>('Tipo', HELP_TEXT.systemRole),
            cell: (info) => {
                const system = isSystem(info.row.original);
                return (
                    <Badge variant={system ? 'secondary' : 'default'}>
                        {system ? 'Sistema' : 'Personalizado'}
                    </Badge>
                );
            },
        },
        {
            id: 'permissions',
            accessorFn: (row) => row.permissions?.length ?? 0,
            header: headerWithHint<T>('Permisos', HELP_TEXT.permission),
            enableGlobalFilter: false,
        },
        {
            id: 'actions',
            header: 'Acciones',
            enableSorting: false,
            enableGlobalFilter: false,
            cell: (info) => renderActions(info.row.original, isSystem(info.row.original)),
        },
    ];
}

/** Columnas de la tabla de roles de una organización, con las etiquetas de su resolvedor. */
export function buildRoleColumns({
    labels,
    renderActions,
}: RoleColumnsOptions): ColumnDef<TenantRoleListItemLocal>[] {
    return buildRoleColumnsCore<TenantRoleListItemLocal>({
        getName: (row) => row.roleName,
        getScopeLabel: (row) => labels.scope(row.scope ?? 'TENANT'),
        isSystem: (row) => isTenantSystemRole(row),
        renderActions,
    });
}
